import { currencyPrecision, dateRangeError, parseDateInput } from '@/components/ui/fieldData';
import type { EntityKind } from '@/components/ui/entityIcons';
import type { Attachment } from '@/lib/attachments';

export type FieldType =
  | 'text'
  | 'multiline'
  | 'number'
  | 'money'
  | 'date'
  | 'period'
  | 'deadline'
  | 'person'
  | 'status'
  | 'select'
  | 'tags'
  | 'entity'
  | 'file'
  | 'link'
  | 'email'
  | 'phone'
  | 'checkbox'
  | 'secret'
  | 'importance'
  | `custom:${string}`;
export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };
export type FieldTone = 'neutral' | 'success' | 'warn' | 'urgent' | 'info';
export interface FieldOption {
  id: string;
  label: string;
  tone?: FieldTone;
  disabled?: boolean;
  level?: 1 | 2 | 3;
}
export interface EntityReference {
  id: string;
  label: string;
  kind?: EntityKind;
  path?: string;
}
interface FieldBase {
  id: string;
  label: string;
  help?: string;
  required?: boolean;
  readOnly?: boolean;
}
export type FieldDefinition =
  | (FieldBase & { type: 'text' | 'multiline'; maxLength?: number })
  | (FieldBase & { type: 'number'; min?: number; max?: number })
  | (FieldBase & { type: 'money'; currency: string })
  | (FieldBase & { type: 'date' })
  | (FieldBase & { type: 'period' })
  | (FieldBase & { type: 'deadline'; timeZone?: string })
  | (FieldBase & { type: 'person' | 'entity'; options: readonly EntityReference[] })
  | (FieldBase & { type: 'status' | 'select' | 'importance'; options: readonly FieldOption[] })
  | (FieldBase & { type: 'tags'; maxTags?: number })
  | (FieldBase & { type: 'file' | 'link' | 'email' | 'phone' | 'checkbox' | 'secret' })
  | (FieldBase & { type: `custom:${string}`; config?: JsonValue });

export type FieldValueByType = {
  text: string;
  multiline: string;
  number: number;
  money: { minorUnits: number; currency: string };
  date: string;
  period: { from: string; to: string };
  deadline: string;
  person: EntityReference;
  status: string;
  select: string;
  tags: string[];
  entity: EntityReference;
  file: Attachment[];
  link: string;
  email: string;
  phone: string;
  checkbox: boolean;
  secret: { configured: boolean };
  importance: string;
};
export type TypedFieldValue =
  | {
      [K in keyof FieldValueByType]: { type: K; value: FieldValueByType[K] | null };
    }[keyof FieldValueByType]
  | { type: `custom:${string}`; value: JsonValue };
export type FieldState =
  { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; value: TypedFieldValue };

const TYPE_MISMATCH = 'Значение не соответствует типу поля.';
const REQUIRED = 'Заполни обязательное поле.';
const INVALID_ISO_DATE = 'Укажи существующую дату в формате YYYY-MM-DD.';
const MAX_SAFE = Number.MAX_SAFE_INTEGER;
const entityKinds = new Set<EntityKind>([
  'host',
  'vm',
  'location',
  'user',
  'channel',
  'ticket',
  'incident',
  'mail',
  'task',
  'account',
  'mention',
]);
const knownTypes = new Set<FieldType>([
  'text',
  'multiline',
  'number',
  'money',
  'date',
  'period',
  'deadline',
  'person',
  'status',
  'select',
  'tags',
  'entity',
  'file',
  'link',
  'email',
  'phone',
  'checkbox',
  'secret',
  'importance',
]);
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isIsoDate(value: unknown): value is string {
  return (
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/u.test(value) && parseDateInput(value).day === value
  );
}
function isSafeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= MAX_SAFE;
}
function isSafeWebUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && !!parsed.hostname;
  } catch {
    return false;
  }
}
function nonemptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
function valueIsEmpty(type: FieldType, value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (type === 'text' || type === 'multiline' || type === 'link' || type === 'email' || type === 'phone') {
    return typeof value === 'string' && value.length === 0;
  }
  if (type === 'tags' || type === 'file') return Array.isArray(value) && value.length === 0;
  return false; // 0 and false are real values.
}
function optionError(options: readonly FieldOption[], id: unknown): string | null {
  if (typeof id !== 'string') return 'Выбери значение из списка.';
  const found = options.find((option) => option.id === id);
  if (!found) return 'Выбранное значение больше недоступно.';
  if (found.disabled) return 'Это значение нельзя выбрать.';
  return null;
}
function isSafeEntityPath(path: unknown): path is string {
  return (
    typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    path.charCodeAt(1) !== 92 &&
    !Array.from(path).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
  );
}
function entityError(options: readonly EntityReference[], value: unknown): string | null {
  if (!isRecord(value) || typeof value.id !== 'string' || !nonemptyString(value.label)) {
    return 'Выбери существующую сущность.';
  }
  if (value.kind !== undefined && !entityKinds.has(value.kind as EntityKind))
    return 'Неизвестный тип сущности.';
  if (value.path !== undefined && !isSafeEntityPath(value.path)) {
    return 'Путь сущности должен быть локальным адресом.';
  }
  const found = options.find((option) => option.id === value.id);
  if (!found) return 'Выбранная сущность больше недоступна.';
  return null;
}
function attachmentError(value: unknown): string | null {
  if (!Array.isArray(value)) return 'Список файлов имеет неверный формат.';
  const ids = new Set<string>();
  for (const file of value) {
    if (
      !isRecord(file) ||
      !nonemptyString(file.id) ||
      !nonemptyString(file.name) ||
      typeof file.mime !== 'string'
    ) {
      return 'У файла должны быть идентификатор, имя и MIME-тип.';
    }
    if (ids.has(file.id)) return 'Идентификаторы файлов должны быть уникальны.';
    ids.add(file.id);
    if (!Number.isSafeInteger(file.size) || (file.size as number) < 0) return 'Размер файла задан неверно.';
    if (!['uploading', 'ready', 'error'].includes(String(file.state)))
      return 'Состояние файла задано неверно.';
    if (
      file.progress !== undefined &&
      (!isSafeNumber(file.progress) || file.progress < 0 || file.progress > 100)
    ) {
      return 'Ход загрузки файла должен быть от 0 до 100.';
    }
  }
  return null;
}
function deadlineError(raw: unknown): string | null {
  if (typeof raw !== 'string') return 'Укажи дату и время с часовым поясом.';
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/u.exec(raw);
  if (!match || !isIsoDate(match[1])) return 'Укажи RFC 3339 дату и время с явным смещением.';
  const hour = Number(match[2]),
    minute = Number(match[3]),
    second = Number(match[4]);
  if (hour > 23 || minute > 59 || second > 59) return 'Время задано неверно.';
  if (match[6] !== 'Z') {
    const offsetHour = Number(match[6].slice(1, 3)),
      offsetMinute = Number(match[6].slice(4, 6));
    if (offsetHour > 23 || offsetMinute > 59) return 'Смещение часового пояса задано неверно.';
  }
  return Number.isFinite(Date.parse(raw)) ? null : 'Укажи существующую дату и время.';
}
function isJsonValue(value: unknown, depth = 0): value is JsonValue {
  if (depth > 32) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every((item) => isJsonValue(item, depth + 1));
  return isRecord(value) && Object.values(value).every((item) => isJsonValue(item, depth + 1));
}

/** Checks a definition/value pair without coercing or mutating the supplied data. */
export function validateFieldValue(definition: FieldDefinition, input: unknown): string | null {
  if (!isRecord(input) || input.type !== definition.type) return TYPE_MISMATCH;
  if (definition.required && (input.value === null || input.value === undefined)) return REQUIRED;
  if (
    definition.required &&
    (definition.type === 'text' || definition.type === 'multiline') &&
    typeof input.value === 'string' &&
    !input.value.trim()
  )
    return REQUIRED;
  if (
    definition.required &&
    definition.type === 'secret' &&
    isRecord(input.value) &&
    input.value.configured === false
  )
    return REQUIRED;
  if (typeof definition.type === 'string' && definition.type.startsWith('custom:')) {
    return isJsonValue(input.value) ? null : 'Пользовательское значение должно быть JSON-совместимым.';
  }
  if (!knownTypes.has(definition.type as FieldType)) return TYPE_MISMATCH;
  const type = definition.type as FieldType;
  const value = input.value;
  if (valueIsEmpty(type, value)) return definition.required ? REQUIRED : null;
  if (value === null || value === undefined) return definition.required ? REQUIRED : null;

  switch (definition.type) {
    case 'text':
    case 'multiline':
      if (typeof value !== 'string') return TYPE_MISMATCH;
      return definition.maxLength !== undefined && value.length > definition.maxLength
        ? `Максимальная длина — ${definition.maxLength} символов.`
        : null;
    case 'number':
      if (!isSafeNumber(value)) return 'Число должно быть конечным и находиться в безопасном диапазоне.';
      if (definition.min !== undefined && value < definition.min)
        return `Значение должно быть не меньше ${definition.min}.`;
      if (definition.max !== undefined && value > definition.max)
        return `Значение должно быть не больше ${definition.max}.`;
      return null;
    case 'money':
      if (
        !isRecord(value) ||
        !Number.isSafeInteger(value.minorUnits) ||
        value.currency !== definition.currency
      ) {
        return 'Сумма должна содержать безопасное целое число минимальных единиц и валюту поля.';
      }
      return currencyPrecision(definition.currency) === null ? 'Неизвестная валюта.' : null;
    case 'date':
      return isIsoDate(value) ? null : INVALID_ISO_DATE;
    case 'period':
      if (!isRecord(value) || typeof value.from !== 'string' || typeof value.to !== 'string')
        return TYPE_MISMATCH;
      if (!isIsoDate(value.from) || !isIsoDate(value.to)) return INVALID_ISO_DATE;
      return dateRangeError(value.from, value.to);
    case 'deadline':
      return deadlineError(value);
    case 'person':
    case 'entity':
      return entityError(definition.options, value);
    case 'status':
    case 'select':
    case 'importance':
      return optionError(definition.options, value);
    case 'tags': {
      if (!Array.isArray(value) || !value.every((tag) => typeof tag === 'string' && tag.trim().length > 0)) {
        return 'Теги должны быть непустыми строками.';
      }
      const maxTags = definition.maxTags ?? 8;
      if (value.length > maxTags) return `Можно добавить не больше ${maxTags} тегов.`;
      if (value.some((tag) => tag.length > 40)) return 'Длина тега не должна превышать 40 символов.';
      const normalized = value.map((tag) => tag.trim().toLocaleLowerCase('ru-RU'));
      if (new Set(normalized).size !== normalized.length) return 'Теги не должны повторяться.';
      return null;
    }
    case 'file':
      return attachmentError(value);
    case 'link':
      return typeof value === 'string' && isSafeWebUrl(value)
        ? null
        : 'Ссылка должна использовать HTTP или HTTPS.';
    case 'email':
      return typeof value === 'string' && /^[^\s@<>]+@[^\s@<>.]+(?:\.[^\s@<>.]+)+$/u.test(value)
        ? null
        : 'Укажи корректный адрес почты.';
    case 'phone': {
      if (typeof value !== 'string' || !/^\+?[0-9().\s-]+$/u.test(value))
        return 'Укажи корректный номер телефона.';
      const digits = value.replace(/\D/gu, '');
      return digits.length >= 7 && digits.length <= 15
        ? null
        : 'Номер телефона должен содержать от 7 до 15 цифр.';
    }
    case 'checkbox':
      return typeof value === 'boolean' ? null : TYPE_MISMATCH;
    case 'secret':
      return isRecord(value) &&
        typeof value.configured === 'boolean' &&
        Object.keys(value).every((key) => key === 'configured')
        ? null
        : 'Секрет может содержать только признак наличия; секретные данные сюда не передаются.';
    default:
      return TYPE_MISMATCH;
  }
}

/** Validates schema identifiers and settings without rewriting the source definitions. */
export function fieldSchemaError(definitions: readonly FieldDefinition[]): string | null {
  if (!Array.isArray(definitions)) return 'Схема полей имеет неверный формат.';
  const ids = new Set<string>();
  for (const definition of definitions) {
    if (typeof definition !== 'object' || definition === null || Array.isArray(definition))
      return 'Описание поля имеет неверный формат.';
    if (!nonemptyString(definition.id) || !nonemptyString(definition.label))
      return 'У каждого поля должны быть непустые id и label.';
    if (ids.has(definition.id)) return `Идентификатор поля повторяется: ${definition.id}.`;
    ids.add(definition.id);
    switch (definition.type) {
      case 'text':
      case 'multiline':
        if (
          definition.maxLength !== undefined &&
          (!Number.isSafeInteger(definition.maxLength) || definition.maxLength < 0)
        )
          return 'Ограничение длины поля задано неверно.';
        break;
      case 'number':
        if (definition.min !== undefined && !isSafeNumber(definition.min))
          return 'Минимум поля задан неверно.';
        if (definition.max !== undefined && !isSafeNumber(definition.max))
          return 'Максимум поля задан неверно.';
        if (definition.min !== undefined && definition.max !== undefined && definition.min > definition.max)
          return 'Минимум поля не может быть больше максимума.';
        break;
      case 'money':
        if (currencyPrecision(definition.currency) === null) return 'Валюта поля задана неверно.';
        break;
      case 'deadline':
        if (definition.timeZone !== undefined && !validZone(definition.timeZone))
          return 'Часовой пояс поля задан неверно.';
        break;
      case 'tags':
        if (
          definition.maxTags !== undefined &&
          (!Number.isSafeInteger(definition.maxTags) || definition.maxTags < 0)
        )
          return 'Лимит тегов задан неверно.';
        break;
      case 'status':
      case 'select':
      case 'importance': {
        if (!Array.isArray(definition.options)) return 'Список вариантов поля задан неверно.';
        const optionIds = new Set<string>(),
          optionLabels = new Set<string>();
        for (const option of definition.options) {
          if (!isRecord(option) || !nonemptyString(option.id) || !nonemptyString(option.label))
            return `У опции поля «${definition.label}» должны быть непустые id и label.`;
          if (option.disabled !== undefined && typeof option.disabled !== 'boolean')
            return 'Признак недоступной опции задан неверно.';
          if (
            option.tone !== undefined &&
            !['neutral', 'success', 'warn', 'urgent', 'info'].includes(String(option.tone))
          )
            return 'Тон опции задан неверно.';
          if (option.level !== undefined && ![1, 2, 3].includes(Number(option.level)))
            return 'Уровень опции задан неверно.';
          if (optionIds.has(option.id) || optionLabels.has(option.label))
            return `Опции поля «${definition.label}» должны иметь уникальные id и подписи.`;
          optionIds.add(option.id);
          optionLabels.add(option.label);
        }
        break;
      }
      case 'person':
      case 'entity': {
        if (!Array.isArray(definition.options)) return 'Список сущностей поля задан неверно.';
        const optionIds = new Set<string>();
        for (const option of definition.options) {
          if (!isRecord(option) || !nonemptyString(option.id) || !nonemptyString(option.label))
            return `У сущности поля «${definition.label}» должны быть непустые id и label.`;
          if (option.kind !== undefined && !entityKinds.has(option.kind as EntityKind))
            return 'Тип сущности в списке задан неверно.';
          if (option.path !== undefined && !isSafeEntityPath(option.path))
            return 'Путь сущности в списке задан неверно.';
          if (optionIds.has(option.id))
            return `Сущности поля «${definition.label}» должны иметь уникальные id.`;
          optionIds.add(option.id);
        }
        break;
      }
      case 'date':
      case 'period':
      case 'file':
      case 'link':
      case 'email':
      case 'phone':
      case 'checkbox':
      case 'secret':
        break;
      default:
        if (typeof definition.type !== 'string' || !definition.type.startsWith('custom:'))
          return 'Тип поля не поддерживается.';
        if (definition.config !== undefined && !isJsonValue(definition.config))
          return 'Настройки custom-поля должны быть JSON-совместимыми.';
    }
  }
  return null;
}

function validZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone }).format(0);
    return true;
  } catch {
    return false;
  }
}
function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}
function wallParts(epochMs: number, timeZone: string): { day: string; time: string } | null {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      calendar: 'gregory',
      numberingSystem: 'latn',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(epochMs));
    const get = (key: string) => parts.find((part) => part.type === key)?.value ?? '';
    const year = Number(get('year')),
      month = Number(get('month')),
      day = Number(get('day'));
    const hour = Number(get('hour')),
      minute = Number(get('minute'));
    if (!year || !month || !day || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return { day: `${pad(year, 4)}-${pad(month)}-${pad(day)}`, time: `${pad(hour)}:${pad(minute)}` };
  } catch {
    return null;
  }
}
function parseInstant(value: string): number | null {
  if (deadlineError(value)) return null;
  const instant = Date.parse(value);
  return Number.isFinite(instant) ? instant : null;
}
export function getZonedInput(at: string, timeZone: string): { day: string; time: string } | null {
  if (!validZone(timeZone)) return null;
  const instant = parseInstant(at);
  return instant === null ? null : wallParts(instant, timeZone);
}
function wallEpoch(day: string, time: string): number | null {
  if (!isIsoDate(day) || !/^([01]\d|2[0-3]):[0-5]\d$/u.test(time)) return null;
  const [year, month, date] = day.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const d = new Date(0);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCFullYear(year, month - 1, date);
  d.setUTCHours(hour, minute, 0, 0);
  return d.getTime();
}
function offsetAt(epochMs: number, timeZone: string): number | null {
  const parts = wallParts(epochMs, timeZone);
  if (!parts) return null;
  const naive = wallEpoch(parts.day, parts.time);
  return naive === null ? null : naive - epochMs;
}
function offsetLabel(offsetMs: number): string {
  const sign = offsetMs < 0 ? '-' : '+';
  const minutes = Math.abs(offsetMs) / 60000;
  return `UTC${sign}${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}
export function resolveZonedInput(
  day: string,
  time: string,
  timeZone: string,
): { choices: { at: string; label: string }[]; error: string | null } {
  const fail = (error: string) => ({ choices: [] as { at: string; label: string }[], error });
  const target = wallEpoch(day, time);
  if (target === null) return fail('Укажи существующие дату и время в формате YYYY-MM-DD и HH:mm.');
  if (!validZone(timeZone)) return fail('Неизвестный часовой пояс.');
  const offsets = new Set<number>();
  const probeStep = 6 * 60 * 60 * 1000;
  for (let delta = -36 * 60 * 60 * 1000; delta <= 36 * 60 * 60 * 1000; delta += probeStep) {
    const offset = offsetAt(target + delta, timeZone);
    if (offset !== null) offsets.add(offset);
  }
  const choices = [...offsets]
    .map((offset) => ({ epoch: target - offset, offset }))
    .filter(({ epoch }) => {
      const local = wallParts(epoch, timeZone);
      return local?.day === day && local.time === time;
    })
    .sort((a, b) => a.epoch - b.epoch)
    .filter((entry, index, all) => index === 0 || entry.epoch !== all[index - 1].epoch)
    .map(({ epoch, offset }) => ({ at: new Date(epoch).toISOString(), label: offsetLabel(offset) }));
  return choices.length
    ? { choices, error: null }
    : fail('Такого местного времени нет из-за перехода часового пояса.');
}
